
import type { EmpireSelectPrompt } from "../net/messages";
import { INTRO_ART, installIntroStyle, introGround, parseEventSet } from "./intro";
import { styleInstaller } from "./chrome";

export interface EmpireSelectActions {
  onSelect?: (empire: number) => void;
  /** Return to login through the host's phase transition. */
  onExit?: () => void;
}

const STYLE_ID = "empire-select-style";

/** `d:/ymir work/ui/intro/`, where `selectempirewindow.py` gets this screen's
 *  ground and its two cycle buttons. */
const INTRO_PATTERN = "/ui/textures/intro/pattern";
const INTRO_SELECT = "/ui/textures/intro/select";

const STYLE = `
/* Over every game-phase window built before sign-in, the minimap first:
   the empire phase draws none (introempire.py), as .charcreate stands at 70. */
.empire-select { z-index: 70; }
.empire-select .intro-anchor { width: min(92%, 560px); }
/* BackGround tiles background_pattern between the two 42px bands, Alpha
   stretches background_alpha (100x69) over the whole screen, and Top_Line and
   Bottom_Line tile line_pattern across those bands
   (locale/en/ui/selectempirewindow.py:20-70). */
.empire-select .intro-stage {
  background-image:
    url("${INTRO_SELECT}/background_alpha.png"),
    url("${INTRO_PATTERN}/background_pattern.png");
  background-repeat: no-repeat, repeat;
  background-position: left top, left 42px;
  background-size: 100% 100%, 128px 128px;
  image-rendering: pixelated;
}
.empire-select .intro-stage::before,
.empire-select .intro-stage::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  height: 42px;
  background: url("${INTRO_PATTERN}/line_pattern.png") repeat-x left top / 64px 42px;
  pointer-events: none;
}
.empire-select .intro-stage::before { top: 0; }
.empire-select .intro-stage::after { bottom: 0; }
/* flex-shrink:0 is load-bearing, not tidiness. .intro-anchor is a flex column
   with a max-height, so a panel taller than the room left is *compressed* by
   default -- and this panel's own children keep their heights, so they spill
   out of it. The map's absolutely positioned territory buttons then paint over
   the kingdom buttons below and swallow their clicks, which is exactly what
   happened the first time the left/right cycler and the lore pager were added
   (playwright: "empire-select-hotspot ... intercepts pointer events"). Keep the
   natural height and let the anchor's own overflow-y do the scrolling. */
.empire-select-panel { padding: 20px; text-align: center; flex: 0 0 auto; }
.empire-select-title {
  display: block;
  width: min(100%, 440px);
  height: auto;
  margin: 0 auto 8px;
}
.empire-select-desc {
  color: var(--intro-ink-dim);
  text-align: left;
  white-space: pre-wrap;
  /* text_board is a fixed 189x122 bar (selectempirewindow.py:242-250) and the
     pager exists because the lore does not fit in it -- so the box holds five
     lines and does not grow. Five lines at the line-height below is 6.75em,
     which is the height DESC_PAGE_LINES actually needs; 6.5em was two lines of
     prose and three blank ones, because the parser kept the file's newlines. */
  min-height: 6.75em;
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.35;
}
/* A row, not a column: three kingdom names side by side is one glance, and it
   keeps the panel short enough that the whole board fits the stage without the
   anchor scrolling. */
.empire-select-options { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
/* Keep each kingdom choice on the source sprite's native dimensions. The
   imported 88x21 large button (tools/import_intro_ui.py:421-429) is the same
   texture bound by LoginWindow (loginwindow.py:597-622); letting the grid
   stretch it to the panel width smears its stone caps. */
.empire-select-choice {
  /* 88px is the sprite's own width, but EMPIRE_A is "Shinsoo Kingdom" and the
     label no longer fits in it. The texture is a horizontal 3-slice (see
     .intro-button in intro.ts), so widening stretches the middle and leaves
     both stone caps intact -- which is exactly what the slice is for. */
  min-width: 88px;
  height: 21px;
  min-height: 21px;
  padding: 0 10px;
  font-size: 10px;
  line-height: 19px;
  white-space: nowrap;
}
.empire-select-choice:hover,
.empire-select-choice.is-hovered,
.empire-select-choice[aria-pressed="true"] {
  border-color: var(--intro-button-top);
  background: rgba(90, 70, 26, 0.6);
  color: var(--intro-gold);
}
/* selectempirewindow.py:92-150's Atlas node -- the world map -- and its
   EmpireArea_ and EmpireAreaFlag_ children (written out rather than glob-joined:
   an asterisk immediately before a slash closes a CSS comment, and this comment
   used to do exactly that -- everything after it, this rule's own width and
   aspect-ratio included, was dropped, so the atlas rendered at its natural size
   over the whole panel and swallowed every button under it), cut by
   tools/import_intro_ui.py's
   empire_sprite into empire_atlas.png (495x357) and nine overlay sprites.
   Sized in CSS percent of the atlas's own 495x357 so the map scales with
   .empire-select-map's width instead of assuming a fixed canvas -- the
   original's own ATALS_X/ATALS_Y do the equivalent scaling against a fixed
   800x600, which this port has no equivalent constant for. */
.empire-select-map {
  position: relative;
  width: min(100%, 300px);
  aspect-ratio: 495 / 357;
  margin: 0 auto 10px;
  border-radius: 2px;
  overflow: hidden;
}
.empire-select-atlas { position: absolute; inset: 0; width: 100%; height: 100%; }
/* introempire.py's OnOverInEmpire/OnOverOutEmpire/OnSelectEmpire animate these
   toward 0 or 1 over several frames (__UpdateAlpha); a CSS transition is the
   same idea without re-deriving its per-frame lerp. */
.empire-select-area,
.empire-select-areaflag {
  position: absolute;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}
.empire-select-area.is-lit,
.empire-select-areaflag.is-lit { opacity: 1; }
/* introempire.py:103-114 (__CreateButtons) drops an EmpireButton over every
   EmpireArea_* image, at that image's own position and size, and sets the
   image's alpha to 0. Clicking the territory *is* how a kingdom is picked in
   the original; this map used to be aria-hidden decoration with the three
   choices living only in a button column beside it. */
.empire-select-hotspot {
  position: absolute;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.empire-select-hotspot:focus-visible { outline: 1px solid var(--intro-gold); }
.empire-select-hotspot[hidden] { display: none; }
/* The kingdom's name is the button's accessible name; the visible label is the
   territory highlight itself, exactly as the original's EmpireButton has no
   text of its own (introempire.py:20-31). */
.empire-select-hotspot-name {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
.empire-select-area,
.empire-select-areaflag { width: 100%; height: 100%; }
.empire-select-hotspot > .empire-select-area { inset: 0; }
/* flag_board sits at y=17 and text_board at y=146 inside empire_board
   (selectempirewindow.py:194-250): the flag is above the lore, not under it. */
.empire-select-flagchip { display: flex; justify-content: center; margin: 0 0 10px; min-height: 26px; }
.empire-select-flag { height: 26px; width: auto; }
/* left_button/right_button, selectempirewindow.py:157-178: a pair under the
   atlas that cycles empireID (introempire.py:202-214). */
.empire-select-cycle { display: flex; justify-content: center; gap: 10px; margin: 0 0 12px; }
.empire-select-cycle .intro-button {
  width: 82px;
  height: 28px;
  min-height: 28px;
  padding: 0;
  border: 0;
  border-image: none;
  font-size: 0;
  color: transparent;
  background: no-repeat left top;
  image-rendering: pixelated;
}
.empire-select-left { background-image: url("${INTRO_SELECT}/left_button_01.png"); }
.empire-select-left:hover:not(:disabled) {
  background-image: url("${INTRO_SELECT}/left_button_02.png");
}
.empire-select-left:active:not(:disabled) {
  background-image: url("${INTRO_SELECT}/left_button_03.png");
}
.empire-select-right { background-image: url("${INTRO_SELECT}/right_button_01.png"); }
.empire-select-right:hover:not(:disabled) {
  background-image: url("${INTRO_SELECT}/right_button_02.png");
}
.empire-select-right:active:not(:disabled) {
  background-image: url("${INTRO_SELECT}/right_button_03.png");
}
/* prev_text_button/next_text_button live inside text_board at x=95/140, y=95 of
   its 189x122 box (selectempirewindow.py:254-279) -- bottom-right of the lore. */
.empire-select-desc-nav { display: flex; justify-content: flex-end; gap: 6px; margin: -8px 0 12px; }
.empire-select-desc-nav .intro-button {
  width: 58px; height: 18px; min-height: 18px; padding: 0 4px; font-size: 9px; line-height: 16px;
}
.empire-select-error { color: var(--intro-danger); min-height: 1.4em; margin: 16px 0 0; }
/* select_button sits left of exit_button in the source board
   (selectempirewindow.py:333-357: x 14 then x 105), both the same
   Large_Button sprite -- kept in that left-to-right order here. */
.empire-select-actions { display: flex; justify-content: center; gap: 8px; margin-top: 14px; }
.empire-select-choose,
.empire-select-exit { width: 88px; height: 21px; min-height: 21px; padding: 0 5px; font-size: 10px; line-height: 19px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export const EMPIRES = ["Shinsoo Kingdom", "Chunjo Kingdom", "Jinno Kingdom"] as const;

export const EMPIRE_TEXT = {
  select: "Choose",
  exit: "Exit",
  prev: "Back",
  next: "Next",
} as const;

export const DESC_PAGE_LINES = 5;
const EMPIRE_DESC_URLS = [
  "",
  `${INTRO_ART}/empiredesc_a.txt`,
  `${INTRO_ART}/empiredesc_b.txt`,
  `${INTRO_ART}/empiredesc_c.txt`,
] as const;

/** `empiredesc_*.txt` as `introempire.py:137-144` feeds `RegisterEventSet`. */
export function parseEmpireDesc(raw: string): string {
  return parseEventSet(raw);
}

const EMPIRE_FLAG_URLS = [
  "",
  `${INTRO_ART}/empire_flag_a.png`,
  `${INTRO_ART}/empire_flag_b.png`,
  `${INTRO_ART}/empire_flag_c.png`,
] as const;

interface MapRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const AREA_RECT: Record<number, MapRect> = {
  1: { left: 8.687, top: 56.303, width: 63.232, height: 34.174 }, // EmpireArea_A x43 y201 w313 h122
  2: { left: 3.434, top: 4.482, width: 38.384, height: 40.056 }, // EmpireArea_B x17 y16 w190 h143
  3: { left: 63.434, top: 9.244, width: 34.141, height: 53.221 }, // EmpireArea_C x314 y33 w169 h190
};

/** `selectempirewindow.py:132-163`'s `EmpireAreaFlag_A/B/C`, same space as `AREA_RECT`. */
const AREAFLAG_RECT: Record<number, MapRect> = {
  1: { left: 33.737, top: 65.826, width: 16.162, height: 19.888 }, // x167 y235 w80 h71
  2: { left: 14.141, top: 11.765, width: 15.96, height: 19.608 }, // x70 y42 w79 h70
  3: { left: 72.121, top: 21.849, width: 16.162, height: 19.888 }, // x357 y78 w80 h71
};

function rectStyle(rect: MapRect): string {
  return `left:${rect.left}%;top:${rect.top}%;width:${rect.width}%;height:${rect.height}%`;
}

export class EmpireSelectScreen {
  private readonly root: HTMLElement;
  private readonly error: HTMLElement;
  private readonly desc: HTMLElement;
  private readonly choose: HTMLButtonElement;
  private readonly choices: HTMLButtonElement[] = [];
  private readonly lore: string[] = ["", "", "", ""];
  private readonly areas = new Map<number, HTMLElement>();
  private readonly areaFlags = new Map<number, HTMLElement>();
  private readonly hotspots = new Map<number, HTMLButtonElement>();
  private readonly flagChip: HTMLImageElement;
  private readonly offered: number[] = [];
  /** First visible line of the lore, in `DESC_PAGE_LINES` steps. */
  private descLine = 0;

  private hovered: number | null = null;

  private selected: number | null = null;
  private pending = false;

  constructor(prompt: EmpireSelectPrompt, options: EmpireSelectActions = {}) {
    installIntroStyle(document);
    installStyle(document);
    const { ground, anchor } = introGround(document, "select");
    this.root = ground;
    this.root.classList.add("empire-select");
    anchor.innerHTML = `
      <section class="intro-board empire-select-panel">
        <img class="empire-select-title" src="${INTRO_ART}/empire_title.png" alt="" />
        <div class="empire-select-map">
          <img class="empire-select-atlas" src="${INTRO_ART}/empire_atlas.png" alt="" />
          <button type="button" class="empire-select-hotspot" data-empire="1"
                  style="${rectStyle(AREA_RECT[1])}" aria-pressed="false">
            <span class="empire-select-hotspot-name">${EMPIRES[0]}</span>
            <img class="empire-select-area" data-empire="1" alt="" src="${INTRO_ART}/empire_area_a.png" />
          </button>
          <button type="button" class="empire-select-hotspot" data-empire="2"
                  style="${rectStyle(AREA_RECT[2])}" aria-pressed="false">
            <span class="empire-select-hotspot-name">${EMPIRES[1]}</span>
            <img class="empire-select-area" data-empire="2" alt="" src="${INTRO_ART}/empire_area_b.png" />
          </button>
          <button type="button" class="empire-select-hotspot" data-empire="3"
                  style="${rectStyle(AREA_RECT[3])}" aria-pressed="false">
            <span class="empire-select-hotspot-name">${EMPIRES[2]}</span>
            <img class="empire-select-area" data-empire="3" alt="" src="${INTRO_ART}/empire_area_c.png" />
          </button>
          <img class="empire-select-areaflag" data-empire="1" alt=""
               src="${INTRO_ART}/empire_areaflag_a.png" style="${rectStyle(AREAFLAG_RECT[1])}" />
          <img class="empire-select-areaflag" data-empire="2" alt=""
               src="${INTRO_ART}/empire_areaflag_b.png" style="${rectStyle(AREAFLAG_RECT[2])}" />
          <img class="empire-select-areaflag" data-empire="3" alt=""
               src="${INTRO_ART}/empire_areaflag_c.png" style="${rectStyle(AREAFLAG_RECT[3])}" />
        </div>
        <div class="empire-select-cycle">
          <button type="button" class="intro-button empire-select-left" aria-label="Previous kingdom">&lsaquo;</button>
          <button type="button" class="intro-button empire-select-right" aria-label="Next kingdom">&rsaquo;</button>
        </div>
        <div class="empire-select-flagchip">
          <img class="empire-select-flag" alt="" hidden />
        </div>
        <p class="empire-select-desc" aria-live="polite"></p>
        <div class="empire-select-desc-nav">
          <button type="button" class="intro-button empire-select-desc-prev">${EMPIRE_TEXT.prev}</button>
          <button type="button" class="intro-button empire-select-desc-next">${EMPIRE_TEXT.next}</button>
        </div>
        <div class="empire-select-options"></div>
        <div class="empire-select-actions">
          <button type="button" class="intro-button empire-select-choose" disabled>${EMPIRE_TEXT.select}</button>
          <button type="button" class="intro-button empire-select-exit">${EMPIRE_TEXT.exit}</button>
        </div>
        <p class="empire-select-error" aria-live="polite"></p>
      </section>`;
    document.body.appendChild(this.root);
    this.error = this.must(".empire-select-error");
    this.desc = this.must(".empire-select-desc");
    this.flagChip = this.must(".empire-select-flag");
    for (const empire of [1, 2, 3]) {
      this.areas.set(empire, this.must(`.empire-select-area[data-empire="${empire}"]`));
      this.areaFlags.set(empire, this.must(`.empire-select-areaflag[data-empire="${empire}"]`));
      this.hotspots.set(
        empire,
        this.must<HTMLButtonElement>(`.empire-select-hotspot[data-empire="${empire}"]`),
      );
    }
    // The shipped words, not invented ones: locale_interface.txt EMPIRE_SELECT
    // "Choose", EMPIRE_EXIT "Exit" -- the same two Large_Button labels
    // `selectempirewindow.py:339,352` put on `select_button`/`exit_button`.
    this.choose = this.must<HTMLButtonElement>(".empire-select-choose");
    this.choose.addEventListener("click", () => {
      // ClickSelectButton (introempire.py:216-218): send the highlighted
      // kingdom, once, and only once a kingdom is actually highlighted.
      if (this.pending || this.selected === null) return;
      this.pending = true;
      this.refreshChoices();
      options.onSelect?.(this.selected);
    });
    this.must(".empire-select-exit").addEventListener("click", () => {
      if (!this.pending) options.onExit?.();
    });
    // OnPressEscapeKey -> ClickExitButton (introempire.py:246-248): Escape is
    // the same action as the visible Exit button, as it already is on the
    // login and character-creation screens this one sits between.
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (!this.pending) options.onExit?.();
    });

    const list = this.must(".empire-select-options");
    let first = 0;
    for (let index = 0; index < EMPIRES.length; index += 1) {
      if ((prompt.available_mask & (1 << index)) === 0) continue;
      const empire = index + 1;
      if (first === 0) first = empire;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "intro-button empire-select-choice";
      button.textContent = EMPIRES[index];
      button.setAttribute("aria-pressed", "false");
      // introempire.py:120-125: hovering an empire only raises that area's own
      // destination alpha; OnOverInEmpire never touches descIndex, so the
      // description stays on whichever kingdom is actually selected. Preview
      // is visual-only here too -- the highlight class -- not a lore swap.
      button.addEventListener("mouseenter", () => {
        button.classList.add("is-hovered");
        this.hovered = empire;
        this.refreshMap();
      });
      button.addEventListener("mouseleave", () => {
        button.classList.remove("is-hovered");
        if (this.hovered === empire) this.hovered = null;
        this.refreshMap();
      });
      button.addEventListener("click", () => {
        // OnSelectEmpire (introempire.py:127-135): highlight this kingdom and
        // show its lore. This never mutates the account by itself -- Choose
        // does that -- so reselecting after a refusal, or just browsing the
        // three descriptions, costs nothing and sends nothing.
        this.pick(empire);
      });
      this.choices.push(button);
      this.offered.push(empire);
      list.appendChild(button);
    }

    for (const [empire, hotspot] of this.hotspots) {
      if (!this.offered.includes(empire)) {
        hotspot.hidden = true;
        continue;
      }
      hotspot.addEventListener("mouseenter", () => {
        this.hovered = empire;
        this.refreshMap();
      });
      hotspot.addEventListener("mouseleave", () => {
        if (this.hovered === empire) this.hovered = null;
        this.refreshMap();
      });
      hotspot.addEventListener("click", () => {
        // EmpireButton.OnMouseLeftButtonDown (introempire.py:29-31) only fires
        // OnSelectEmpire when the clicked kingdom is not already the current
        // one; `pick` is idempotent, so the guard is unnecessary here.
        this.pick(empire);
      });
    }

    // left_button / right_button (selectempirewindow.py:157-178) wired to
    // ClickLeftButton / ClickRightButton (introempire.py:202-214), which walk
    // empireID down and up with a wrap. Missing entirely until now.
    this.must<HTMLButtonElement>(".empire-select-left").addEventListener("click", () => {
      this.cycle(-1);
    });
    this.must<HTMLButtonElement>(".empire-select-right").addEventListener("click", () => {
      this.cycle(1);
    });

    // prev_text_button / next_text_button (selectempirewindow.py:254-279) wired
    // to PrevDescriptionPage / NextDescriptionPage (introempire.py:146-159).
    this.must<HTMLButtonElement>(".empire-select-desc-prev").addEventListener("click", () => {
      this.page(-1);
    });
    this.must<HTMLButtonElement>(".empire-select-desc-next").addEventListener("click", () => {
      this.page(1);
    });

    if (first !== 0) this.pick(first);
    this.refreshChoices();
    this.refreshMap();
    void this.preload();
  }

  private async preload(): Promise<void> {
    try {
      await Promise.all(
        [1, 2, 3].map(async (empire) => {
          const response = await fetch(EMPIRE_DESC_URLS[empire]);
          if (!response.ok) return;
          this.lore[empire] = parseEmpireDesc(await response.text());
        }),
      );
    } catch {
      return;
    }
    // Show whichever kingdom is selected *now*, not whichever was selected
    // when this fetch was kicked off: a player can click a different one
    // before the network round trip finishes.
    if (this.desc.textContent === "" && this.selected !== null) this.showDesc(this.selected);
  }

  private pick(empire: number): void {
    if (this.pending) return;
    this.selected = empire;
    this.descLine = 0;
    for (const [index, choice] of this.choices.entries()) {
      choice.setAttribute("aria-pressed", String(this.offered[index] === empire));
    }
    for (const [id, hotspot] of this.hotspots) {
      hotspot.setAttribute("aria-pressed", String(id === empire));
    }
    this.showDesc(empire);
    this.refreshChoices();
    this.refreshMap();
  }

  /** `ClickLeftButton` / `ClickRightButton` -- `introempire.py:202-214`. */
  private cycle(delta: -1 | 1): void {
    if (this.pending || this.offered.length === 0) return;
    const at = this.selected === null ? -1 : this.offered.indexOf(this.selected);
    const next = (at + delta + this.offered.length) % this.offered.length;
    this.pick(this.offered[next]);
  }

  private page(delta: -1 | 1): void {
    const lines = (this.selected === null ? "" : this.lore[this.selected]).split("\n");
    const last = Math.max(0, lines.length - DESC_PAGE_LINES);
    const next = this.descLine + delta * DESC_PAGE_LINES;
    this.descLine = Math.max(0, Math.min(last, next));
    if (this.selected !== null) this.showDesc(this.selected);
  }

  private showDesc(empire: number): void {
    const text = this.lore[empire];
    if (!text) return;
    const lines = text.split("\n");
    this.desc.textContent = lines
      .slice(this.descLine, this.descLine + DESC_PAGE_LINES)
      .join("\n");
    const prev = this.root.querySelector<HTMLButtonElement>(".empire-select-desc-prev");
    const next = this.root.querySelector<HTMLButtonElement>(".empire-select-desc-next");
    if (prev !== null) prev.disabled = this.descLine === 0;
    if (next !== null) next.disabled = this.descLine + DESC_PAGE_LINES >= lines.length;
  }

  get open(): boolean {
    return this.root.isConnected;
  }

  fail(text: string): void {
    this.release();
    this.error.textContent = text;
  }

  /** Reopen the choices after a refusal the 2014 client showed nothing for. */
  release(): void {
    this.pending = false;
    this.refreshChoices();
  }

  dismiss(): void {
    this.root.remove();
  }

  private must<T extends Element = HTMLElement>(selector: string): T {
    const found = this.root.querySelector<T>(selector);
    if (found === null) throw new Error(`empire select template is missing ${selector}`);
    return found;
  }

  private refreshChoices(): void {
    for (const button of this.choices) button.disabled = this.pending;
    this.choose.disabled = this.pending || this.selected === null;
  }

  private refreshMap(): void {
    for (const [empire, area] of this.areas) {
      area.classList.toggle("is-lit", empire === this.selected || empire === this.hovered);
    }
    for (const [empire, flag] of this.areaFlags) {
      flag.classList.toggle("is-lit", empire === this.selected);
    }
    if (this.selected === null) {
      this.flagChip.hidden = true;
    } else {
      this.flagChip.src = EMPIRE_FLAG_URLS[this.selected];
      this.flagChip.hidden = false;
    }
  }
}
